import classNames from 'classnames';
import { throttle } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';

import { useTranslation } from 'utils/i18n';
// components
import QuickSearch from '../../containers/quickSearch/index';
// context
import { AuthContext } from '../../state/auth';
import { BasketContext } from '../../state/basket/index';
import { MobileHeaderContext } from '../../state/mobileHeader';

const HeaderMobileNav = () => {
  const { t } = useTranslation('common');
  const { actions, loggedIn: isLoggedIn } = useContext(AuthContext);
  const { items } = useContext(BasketContext);
  const {
    stickyNavVisible,
    toggleStickyNavVisibility,
    menuOpen,
    setMenuOpen,
    searchOpen,
    setSearchOpen,
    roomTypesOpen,
  } = useContext(MobileHeaderContext);

  const router = useRouter();

  const [loggedIn, setLoggedIn] = useState(false);

  const headerClasses = classNames({
    'mobile-header': true,
    'mobile-header--sticky': stickyNavVisible,
    'mobile-header--menu-closed': !menuOpen,
    'mobile-header--menu-open': !!menuOpen,
    'mobile-header--search-closed': !searchOpen,
    'mobile-header--search-open': !!searchOpen,
    'mobile-header--room-types-closed': !roomTypesOpen,
    'mobile-header--room-types-open': !!roomTypesOpen,
  });

  const searchClasses = classNames({
    'mobile-room-search': true,
    'mobile-room-search--closed': !searchOpen,
    'mobile-room-search--open': !!searchOpen,
  });

  useEffect(() => {
    const displayStickyNavAt = 0;
    const handleScroll = () => {
      toggleStickyNavVisibility(window.pageYOffset > displayStickyNavAt);
    };
    const throttledHandleScroll = throttle(handleScroll, 100);
    window.addEventListener('scroll', throttledHandleScroll);

    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [toggleStickyNavVisibility]);

  const toggleMenuOpen = () => {
    setMenuOpen(!menuOpen);
    setSearchOpen(false);
  };

  const toggleSearchOpen = () => {
    setSearchOpen(!searchOpen);
    setMenuOpen(false);
  };

  const closeAll = () => {
    setMenuOpen(false);
    setSearchOpen(false);
  };

  useEffect(() => {
    if (isLoggedIn) {
      setLoggedIn(true);
    }
  }, [isLoggedIn]);

  return (
    <div className={headerClasses}>
      <div className="mobile-header__nav" data-cy="mobileHeader">
        <div className="mobile-header__logo mobile-header__logo--non-sticky">
          <Link href="/">
            <img src="/images/openspace-logo.svg" alt="NHS Open Space" />
          </Link>
        </div>
        <div className="mobile-header__logo mobile-header__logo--sticky">
          <Link href="/">
            <img src="/images/openspace-logo.svg" alt="NHS Open Space" />
          </Link>
        </div>

        {loggedIn && (
          <button
            type="button"
            className="mobile-header__basket"
            onClick={closeAll}
          >
            <Link href={t('navigation.basket.url')}>
              <img
                src="/images/icons/homepage/navigation/cart.svg"
                alt={t('navigation.basket.text')}
                data-cy="basketLink"
              />
            </Link>
            {!!items?.length && (
              <span
                className="mobile-header__basket--count"
                data-cy="basketCount"
              >
                {items?.length}
              </span>
            )}
          </button>
        )}

        <button
          type="button"
          className="mobile-header__search-rooms"
          onClick={toggleSearchOpen}
        >
          <img
            src="/images/icons/homepage/navigation/search.svg"
            alt="Magnifying glass"
          />
        </button>

        <button
          type="button"
          className="mobile-header__toggle-menu"
          onClick={toggleMenuOpen}
          data-testid="toggleMenu"
        >
          <img
            src="/images/icons/homepage/navigation/hamburger.svg"
            id="nav-open-icon"
            alt="Navigation open"
          />
          <img
            src="/images/icons/homepage/navigation/close.svg"
            id="nav-close-icon"
            alt="Navigation close"
          />
        </button>
      </div>

      <nav className="mobile-menu">
        <ul className="mobile-menu__list" id="mobile-nav-list">
          <li className="mobile-menu__item">
            <Link href={t('navigation.search.url')} onClick={closeAll}>
              {t('navigation.search.text')}
            </Link>
          </li>
          <li className="mobile-menu__item">
            <Link href={t('navigation.properties.url')} onClick={closeAll}>
              {t('navigation.properties.text')}
            </Link>
          </li>
          <li className="mobile-menu__item">
            <Link href={t('navigation.help.url')} onClick={closeAll}>
              {t('navigation.help.text')}
            </Link>
          </li>

          {loggedIn && (
            <>
              <li className="mobile-menu__item">
                <Link href={t('navigation.accounts.url')} onClick={closeAll}>
                  {t('navigation.accounts.text')}
                </Link>
              </li>
              <li className="mobile-menu__item">
                <Link
                  href="#"
                  onClick={() => {
                    closeAll();
                    actions.signOut();
                  }}
                  className="button button--block button--outline button--outline-secondary button--secondary-text"
                >
                  {t('navigation.sign_out.text')}
                </Link>
              </li>
            </>
          )}

          {!loggedIn && (
            <>
              <li className="mobile-menu__item">
                <Link
                  href={t('navigation.sign_in.url')}
                  onClick={() => {
                    closeAll();
                    if (router.pathname !== t('navigation.sign_in.url'))
                      actions.setReturnUrl(router.asPath);
                  }}
                  className="button button--primary button--block button--outline button--outline-secondary button--secondary-text"
                >
                  {t('navigation.sign_in.text')}
                </Link>
              </li>
              <li className="mobile-menu__item">
                <Link
                  href={t('navigation.register.url')}
                  className="button button--primary button--block button--white-text"
                  onClick={closeAll}
                >
                  {t('navigation.register.text')}
                </Link>
              </li>
            </>
          )}
        </ul>
      </nav>

      <div className={searchClasses} id="#mobile-search">
        <h1 className="mobile-room-search__heading">
          {t('quick_search.heading')}
        </h1>
        <QuickSearch onSubmit={closeAll} />
      </div>
    </div>
  );
};

export default HeaderMobileNav;
